import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { collection, getDocs, query, orderBy, limit, startAfter } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useCategories } from '../context/CategoriesContext';
import { useDiscounts } from '../context/DiscountContext';
import Loader from '../components/Loader';
import DiscountDisplay from '../components/DiscountDisplay';
import LuxuryCashbackDisplay from '../components/LuxuryCashbackDisplay';
import SEOHead from '../components/SEOHead';
import { generateCollectionSEO } from '../utils/seoSchemas';

import './Collections.css';

// Sorting strategy - prioritize New Drop products by priority, then regular products by price
const applyMixedSortingStrategy = (products) => {
  if (!products || products.length === 0) return products;
  
  // Separate New Drop products from regular products
  const newDropProducts = products.filter(product => product.isNewCollection === true);
  const regularProducts = products.filter(product => !product.isNewCollection || product.isNewCollection !== true);
  
  // Sort New Drop products by priority (High > Medium > Low), then by creation date (newest first)
  const priorityOrder = { 'High': 1, 'Medium': 2, 'Low': 3 };
  const sortedNewDropProducts = newDropProducts.sort((a, b) => {
    const aPriority = a.newDropPriority || '';
    const bPriority = b.newDropPriority || '';
    
    // Get priority order (4 = no priority, goes last)
    const aPriorityOrder = priorityOrder[aPriority] || 4;
    const bPriorityOrder = priorityOrder[bPriority] || 4;
    
    // First sort by priority
    if (aPriorityOrder !== bPriorityOrder) {
      return aPriorityOrder - bPriorityOrder; // Lower number = higher priority
    }
    
    // If same priority, sort by creation date (newest first)
    // Handle Firestore Timestamp properly
    let aDate;
    if (a.createdAt?.toDate) {
      aDate = a.createdAt.toDate();
    } else if (a.createdAt) {
      aDate = new Date(a.createdAt);
    } else {
      aDate = new Date(0); // Fallback to epoch if no date
    }
    
    let bDate;
    if (b.createdAt?.toDate) {
      bDate = b.createdAt.toDate();
    } else if (b.createdAt) {
      bDate = new Date(b.createdAt);
    } else {
      bDate = new Date(0); // Fallback to epoch if no date
    }
    
    return bDate - aDate; // Newest first (descending order)
  });
  
  // Regular products are already sorted by price from Firestore, keep that order
  // Combine: New Drop products first, then regular products
  return [...sortedNewDropProducts, ...regularProducts];
};

// Optimized Image Preloader Hook - only loads visible images
const useImagePreloader = () => {
  const [preloadedImages, setPreloadedImages] = useState(new Set());
  const [loadingQueue, setLoadingQueue] = useState(new Set());
  const maxConcurrentLoads = 3; // Reduced from 6 to 3 for better performance
  
  const preloadImage = useCallback((src) => {
    if (!src || preloadedImages.has(src) || loadingQueue.has(src)) {
      return;
    }
    
    // Check if we're at the concurrent limit
    if (loadingQueue.size >= maxConcurrentLoads) {
      // Queue the image for later loading with longer delay
      setTimeout(() => preloadImage(src), 200);
      return;
    }
    
    setLoadingQueue(prev => new Set([...prev, src]));
    
    const img = new Image();
    img.onload = () => {
      setPreloadedImages(prev => new Set([...prev, src]));
      setLoadingQueue(prev => {
        const newQueue = new Set(prev);
        newQueue.delete(src);
        return newQueue;
      });
    };
    img.onerror = () => {
      setLoadingQueue(prev => {
        const newQueue = new Set(prev);
        newQueue.delete(src);
        return newQueue;
      });
    };
    img.src = src;
  }, [preloadedImages, loadingQueue, maxConcurrentLoads]);
  
  // Get the first image URL for preloading (simplified)
  const getFirstImageUrlForPreload = useCallback((product) => {
    if (!product.imageURLs || product.imageURLs.length === 0) return null;
    
    const firstImage = product.imageURLs[0];
    
    // Use optimized 768w for preloading (good balance of quality and size)
    if (firstImage.optimized && firstImage.optimized['768w']) {
      return firstImage.optimized['768w'].url;
    }
    
    // Fallback to original image
    return firstImage.original?.url || null;
  }, []);
  
  const preloadImages = useCallback((products) => {
    // Only preload images for the first 6 products (reduced from 12)
    const productsToPreload = products.slice(0, 6);
    
    productsToPreload.forEach((product, index) => {
      // Stagger the preloading with longer delays to avoid overwhelming the network
      setTimeout(() => {
        const firstUrl = getFirstImageUrlForPreload(product);
        if (firstUrl) preloadImage(firstUrl);
      }, index * 150); // Increased from 50ms to 150ms delay
    });
  }, [preloadImage, getFirstImageUrlForPreload]);
  
  return { preloadImages, preloadedImages };
};

const Collections = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { categories: contextCategories, loading: categoriesLoading } = useCategories();
  const { calculateProductDiscount, discounts, loading: discountsLoading } = useDiscounts();
  const [sortedCategories, setSortedCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [visibleProducts, setVisibleProducts] = useState(new Set());
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(12); // Load 12 products at a time
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastVisible, setLastVisible] = useState(null); // For Firestore pagination
  const [allLoadedProducts, setAllLoadedProducts] = useState([]); // Store all loaded products
  
  // Animation states for 3D flip and swish reveal
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationPhase, setAnimationPhase] = useState('idle'); // 'idle', 'flip-out', 'entrance'
  const [animationKey, setAnimationKey] = useState(0);
  const animationTimeoutRef = useRef(null);
  const isAnimatingRef = useRef(false);
  const hasShownEntranceRef = useRef(false);
  
  // Image preloading for smoother transitions
  const { preloadImages, preloadedImages } = useImagePreloader();
  
  // Filter products based on selected category and product type
  const allFilteredProducts = useMemo(() => 
    allLoadedProducts.filter(product => 
      selectedCategory === 'all' || product.categoryId === selectedCategory
    ), [allLoadedProducts, selectedCategory]
  );
  
  // Products are already sorted by price from Firestore, just filter by category
  const filteredProducts = useMemo(() => 
    allFilteredProducts, 
    [allFilteredProducts]
  );
  
  // Check if there are more products to load
  const hasMore = hasMoreProducts;
  
  // Preload images for next category
  const preloadNextCategoryImages = useCallback((categoryId) => {
    const nextCategoryProducts = products.filter(product => 
      categoryId === 'all' || product.categoryId === categoryId
    );
    preloadImages(nextCategoryProducts);
  }, [products, preloadImages]);
  
  // Categories and discounts loading
  useEffect(() => {
    // Categories and discounts are loaded automatically
  }, [contextCategories, categoriesLoading, sortedCategories, discounts, discountsLoading]);

  // Cleanup animation timeouts on unmount
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);



  // Performance-optimized category change handler with 3D flip only
  const handleCategoryChange = useCallback((categoryId) => {
    if (categoryId === selectedCategory || isAnimatingRef.current) return;
    
    // Start preloading images for the new category immediately
    preloadNextCategoryImages(categoryId);
    
    // Prevent multiple simultaneous animations
    isAnimatingRef.current = true;
    setIsAnimating(true);
    
    // Start 3D Flip Out Animation
    setAnimationPhase('flip-out');
    
    // Wait for flip-out animation to complete (reduced to 400ms for better responsiveness)
    animationTimeoutRef.current = setTimeout(() => {
      // Change category after flip-out is completely done
      setSelectedCategory(categoryId);
      // Don't reset product type when changing categories - preserve it
      setAnimationKey(prev => prev + 1);
      
      // Update URL to reflect the new category and preserve product type
      const newParams = {};
      if (categoryId !== 'all') {
        newParams.category = categoryId;
      }
      if (selectedProductType) {
        newParams.productType = selectedProductType;
      }
      setSearchParams(newParams);
      
      // Start entrance animation immediately for overlap effect
      setAnimationPhase('entrance');
      
      // Smooth scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Close filter dropdown
      setTimeout(() => {
        setIsFilterOpen(false);
      }, 150);
      
      // Complete animation sequence with overlap
      animationTimeoutRef.current = setTimeout(() => {
        setAnimationPhase('idle');
        setIsAnimating(false);
        isAnimatingRef.current = false;
      }, 500); // Entrance animation duration + buffer for overlap
      
    }, 400); // Reduced flip-out duration from 600ms to 400ms for better responsiveness
    
  }, [selectedCategory, preloadNextCategoryImages, setSearchParams]);

  // Entrance animation when first visiting the page
  useEffect(() => {
    if (!loading && !isAnimating && !hasShownEntranceRef.current) {
      // Mark that we've shown entrance animation
      hasShownEntranceRef.current = true;
      
      // No entrance animations for header - keep it fixed
    }
  }, [loading, isAnimating]);


  // Read category and product type from URL parameters when component mounts
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    const productTypeParam = searchParams.get('productType');
    
    if (categoryParam && categoryParam !== 'all') {
      setSelectedCategory(categoryParam);
    } else {
      setSelectedCategory('all');
    }
    
    if (productTypeParam) {
      setSelectedProductType(productTypeParam);
    } else {
      setSelectedProductType(null);
    }
  }, [searchParams, contextCategories]);

  // Handle window resize for responsive images (debounced)
  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWindowWidth(window.innerWidth);
      }, 150); // Debounce resize events
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  // Intersection Observer for priority loading (optimized)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const productId = entry.target.dataset.productId;
            if (productId) {
              setVisibleProducts(prev => new Set([...prev, productId]));
              // Unobserve after loading to improve performance
              observer.unobserve(entry.target);
            }
          }
        });
      },
      {
        rootMargin: '100px 0px', // Start loading 100px before the image comes into view
        threshold: 0.1
      }
    );

    // Observe all product cards
    const productCards = document.querySelectorAll('[data-product-id]');
    productCards.forEach(card => observer.observe(card));

    return () => {
      productCards.forEach(card => observer.unobserve(card));
    };
  }, [filteredProducts]);

  // Fetch initial products from Firestore with pagination
  const fetchInitialProducts = useCallback(async () => {
    try {
      setLoading(true);
      
      // Categories are now fetched from shared context
      // Always create basic categories even if context is empty
      const categoryOrder = ['all', 'shirts', 'pants', 'kurtas', 'blazer', 'ethnic-sets', 'suit-sets', 'co-ord-sets'];
      const sortedCategories = [
        { id: 'all', name: 'ALL COLLECTIONS', icon: '✨' },
        ...(contextCategories.length > 0 ? contextCategories
          .sort((a, b) => {
            const aIndex = categoryOrder.indexOf(a.id);
            const bIndex = categoryOrder.indexOf(b.id);
            if (aIndex === -1) return 1;
            if (bIndex === -1) return 1;
            return aIndex - bIndex;
          })
          .map(cat => ({
            id: cat.id,
            name: cat.categoryName?.toUpperCase() || cat.id.toUpperCase(),
            icon: getCategoryIcon(cat.categoryName || cat.id),
            sizeChartURLs: cat.sizeChartURLs // Include size chart URLs
          })) : [
            // Fallback categories if context is empty - match the categoryOrder
            { id: 'ethnic-sets', name: 'ETHNIC SETS', icon: '🌸' },
            { id: 'shirts', name: 'SHIRTS', icon: '👔' },
            { id: 'pants', name: 'PANTS', icon: '👖' },
            { id: 'blazer', name: 'BLAZER', icon: '🎩' },
            { id: 'suit-sets', name: 'SUIT SETS', icon: '👔' },
            { id: 'kurtas', name: 'KURTAS', icon: '🧥' },
            { id: 'co-ord-sets', name: 'CO-ORD SETS', icon: '👗' }
          ])
      ];
      
      setSortedCategories(sortedCategories);
      
      // Fetch first batch of products with pagination - ordered by basePrice (low to high)
      try {
        const productsQuery = query(
          collection(db, 'products'),
          orderBy('basePrice', 'asc'),
          limit(productsPerPage)
        );
        
        const productsSnapshot = await getDocs(productsQuery);
        const productsData = productsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Apply mixed sorting strategy
        const sortedProducts = applyMixedSortingStrategy(productsData);
        
        // Set initial products
        setAllLoadedProducts(sortedProducts);
        setProducts(sortedProducts); // Keep for backward compatibility
        
        // Set pagination state
        setHasMoreProducts(productsSnapshot.docs.length === productsPerPage);
        setLastVisible(productsSnapshot.docs[productsSnapshot.docs.length - 1]);
        
      } catch (error) {
        console.error('Failed to fetch products from Firebase:', error);
        setAllLoadedProducts([]);
        setProducts([]);
        setHasMoreProducts(false);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setAllLoadedProducts([]);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [productsPerPage]); // Remove contextCategories dependency to prevent refetching

  // Load more products function
  const loadMoreProducts = useCallback(async () => {

    if (loadingMore || !hasMoreProducts || !lastVisible) {

      return;
    }
    
    try {

      setLoadingMore(true);
      
      const productsQuery = query(
        collection(db, 'products'),
        orderBy('basePrice', 'asc'),
        startAfter(lastVisible),
        limit(productsPerPage)
      );
      
      const productsSnapshot = await getDocs(productsQuery);
      const newProductsData = productsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Apply sorting strategy to new products
      const sortedNewProducts = applyMixedSortingStrategy(newProductsData);
      const newProducts = sortedNewProducts;
      
      if (newProducts.length > 0) {
        // Append new products to existing ones first
        setAllLoadedProducts(prev => {
          const combined = [...prev, ...newProducts];
          // Re-sort entire combined list to ensure New Drop products stay at top
          return applyMixedSortingStrategy(combined);
        });
        setProducts(prev => {
          const combined = [...prev, ...newProducts];
          // Re-sort entire combined list to ensure New Drop products stay at top
          return applyMixedSortingStrategy(combined);
        });
        
        // Update pagination state
        setHasMoreProducts(productsSnapshot.docs.length === productsPerPage);
        setLastVisible(productsSnapshot.docs[productsSnapshot.docs.length - 1]);
      } else {
        setHasMoreProducts(false);
      }
    } catch (error) {
      console.error('Error loading more products:', error);
      setHasMoreProducts(false);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMoreProducts, lastVisible, productsPerPage]);

  // Initial data fetch - only run once on mount
  useEffect(() => {
    fetchInitialProducts();
  }, []); // Empty dependency array - only run once on mount

  // Helper function to get category icon
  const getCategoryIcon = (categoryName) => {
    const iconMap = {
      'shirts': '👔',
      'pants': '👖',
      'polos-tshirts': '👕',
      'kurtas': '🧥',
      'blazer': '🎩',
      'ethnic': '💎',
      'ethnic-sets': '💎'
    };
    return iconMap[categoryName?.toLowerCase()] || '✨';
  };

  // Helper function to get the first image URL with responsive sizing (optimized)
  const getFirstImageUrl = useCallback((product) => {
    if (!product.imageURLs || product.imageURLs.length === 0) {
      return null;
    }

    const firstImage = product.imageURLs[0];
    
    // Check if optimized images exist
    if (firstImage.optimized) {
      // Choose the best image size based on window width with fallbacks
      if (windowWidth <= 480) {
        return firstImage.optimized['480w']?.url || firstImage.optimized['768w']?.url || firstImage.original?.url;
      } else if (windowWidth <= 768) {
        return firstImage.optimized['768w']?.url || firstImage.optimized['1080w']?.url || firstImage.original?.url;
      } else if (windowWidth <= 1080) {
        return firstImage.optimized['1080w']?.url || firstImage.optimized['1440w']?.url || firstImage.original?.url;
      } else {
        return firstImage.optimized['1440w']?.url || firstImage.optimized['1920w']?.url || firstImage.original?.url;
      }
    }
    
    // Fallback to original image if no optimized versions
    return firstImage.original?.url || null;
  }, [windowWidth]);

  // Handle product type selection
  const handleProductTypeChange = (productType) => {
    const newProductType = selectedProductType === productType ? null : productType;
    setSelectedProductType(newProductType);
    
    // Update URL to reflect the new product type
    const newParams = {};
    if (selectedCategory !== 'all') {
      newParams.category = selectedCategory;
    }
    if (newProductType) {
      newParams.productType = newProductType;
    }
    setSearchParams(newParams);
  };

  // Infinite scroll handler with throttling
  const handleScroll = useCallback(() => {
    if (loadingMore || !hasMore) {
      return;
    }
    
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    
    // Calculate distance from bottom
    const distanceFromBottom = documentHeight - (scrollTop + windowHeight);
    
    // Load more when user is 300px from bottom (increased threshold)
    if (distanceFromBottom <= 300) {
      loadMoreProducts();
    }
  }, [loadingMore, hasMore, loadMoreProducts, allLoadedProducts.length]);

  // Reset pagination when category changes and load more products if needed
  useEffect(() => {
    setCurrentPage(1);
    setVisibleProducts(new Set());
    
    // Check if we have enough products for the selected category
    const categoryProducts = allLoadedProducts.filter(product => 
      selectedCategory === 'all' || product.categoryId === selectedCategory
    );
    
    // If we don't have enough products for this category, load more
    if (categoryProducts.length < 12 && hasMoreProducts) {
      loadMoreProducts();
    }
  }, [selectedCategory, allLoadedProducts, hasMoreProducts, loadMoreProducts]);

  // Add scroll event listener for infinite scroll
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  // Intersection Observer for more reliable infinite loading
  useEffect(() => {
    if (!hasMore || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadMoreProducts();
          }
        });
      },
      {
        root: null,
        rootMargin: '300px', // Trigger 300px before the element comes into view
        threshold: 0.1
      }
    );

    // Create a sentinel element at the bottom of the products
    const sentinel = document.createElement('div');
    sentinel.id = 'infinite-scroll-sentinel';
    sentinel.style.height = '1px';
    sentinel.style.width = '100%';
    
    // Find the products grid and add sentinel
    const productsGrid = document.querySelector('.products-grid');
    if (productsGrid) {
      productsGrid.appendChild(sentinel);
      observer.observe(sentinel);
    }

    return () => {
      observer.disconnect();
      const sentinelElement = document.getElementById('infinite-scroll-sentinel');
      if (sentinelElement) {
        sentinelElement.remove();
      }
    };
  }, [hasMore, loadingMore, loadMoreProducts]);

  // Generate SEO for collections page
  const collectionSEO = useMemo(() => {
    // If specific category is selected
    if (selectedCategory && selectedCategory !== 'all') {
      const category = sortedCategories.find(cat => cat.id === selectedCategory);
      if (category) {
        return generateCollectionSEO(category, filteredProducts);
      }
    }
    
    // Default SEO for all collections
    return {
      title: "Shop All Collections | Premium Men's Fashion Online | Vastré By Vijh",
      description: `Explore ${allFilteredProducts.length}+ premium men's fashion pieces by Vastré By Vijh. Custom tailored suits, designer kurtas, ethnic wear, and luxury menswear online in India.`,
      keywords: "vastrebyvijh, men's fashion collection, premium menswear online, designer clothing India, custom tailored suits, ethnic wear, luxury fashion",
      image: filteredProducts[0]?.imageURLs?.[0] || '',
      url: "https://vastrebyvijh.com/collections",
      type: "website"
    };
  }, [selectedCategory, sortedCategories, filteredProducts, allFilteredProducts.length]);

  if (loading) {
    return (
      <div className="collections-page">
        <Loader isLoading={true} />
      </div>
    );
  }

  // Don't return early - let the full page render with "Coming Soon" section



  return (
    <>
      {/* SEO Meta Tags */}
      {collectionSEO && <SEOHead {...collectionSEO} />}
      
      <div className="collections-page">

      
      {/* Hero Header */}
      <div className="collections-hero">
        <h1 className="collections-title">Collections</h1>
      </div>



      {/* Luxury Icon Filter System */}
      <div className="luxury-filter-container">
        <div className="filter-header">
          <div className="filter-icon-button" onClick={() => setIsFilterOpen(!isFilterOpen)}>
            <span className="filter-icon-symbol">⚡</span>
            <span className="filter-label">Filters</span>
            <span className={`filter-arrow ${isFilterOpen ? 'open' : ''}`}>▼</span>
          </div>
          
          {/* Active Category Display */}
          {selectedCategory !== 'all' && (
            <div className="active-category-display">
              <span className="active-category-text">
                {sortedCategories.find(cat => cat.id === selectedCategory)?.name}
              </span>
            </div>
          )}
        </div>

        {/* Filter Dropdown */}
        {isFilterOpen && (
          <div className="filter-dropdown">
            {/* Main Categories */}
            <div className="filter-section">
              <h4 className="filter-section-title">Categories</h4>
              <div className="filter-options">
                {sortedCategories.map((category) => (
                  <button
                    key={category.id}
                    className={`filter-option ${selectedCategory === category.id ? 'active' : ''}`}
                    onClick={() => handleCategoryChange(category.id)}
                  >
                    <span className="option-icon">{category.icon}</span>
                    <span className="option-text">{category.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Filter Actions */}
            <div className="filter-actions">
              <div className="action-buttons">
                <button 
                  className="apply-filters-btn"
                  onClick={() => setIsFilterOpen(false)}
                >
                  Apply Filter
                </button>
                <button 
                  className="clear-filters-btn"
                  onClick={() => {
                    setSelectedCategory('all');
                    setSelectedProductType(null);
                    setSearchParams({}); // Clear all URL parameters
                  }}
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="collections-container">
        {/* Left Sidebar Navigation - Hidden on Mobile */}
        <aside className="collections-sidebar">
          <nav className="category-navigation">
            <ul className="category-list">
              {sortedCategories.map((category) => (
                <li key={category.id} className="category-item">
                  <button
                    className={`category-button ${selectedCategory === category.id ? 'active' : ''}`}
                    onClick={() => handleCategoryChange(category.id)}
                  >
                    {category.name}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        {/* Main Content Area */}
        <main className="collections-main">
          
          {filteredProducts.length > 0 ? (
            <div 
              className={`products-grid ${animationPhase === 'flip-out' ? 'flip-out' : ''} ${animationPhase === 'entrance' ? 'entrance' : ''}`}
              key={animationKey}
            >
              {/* Use existing Loader component during transitions */}
              {isAnimating && <Loader />}
              
              {filteredProducts.map((product) => {
              // Find the category data for this product
              let productCategory = sortedCategories.find(cat => cat.id === product.categoryId);
              
              // If exact match fails, try fuzzy matching for common mismatches
              if (!productCategory && product.categoryId) {
                // Try to find category by partial match
                productCategory = sortedCategories.find(cat => {
                  const productCatLower = product.categoryId.toLowerCase();
                  const categoryIdLower = cat.id.toLowerCase();
                  
                  // Check if category ID contains the product category or vice versa
                  return productCatLower.includes(categoryIdLower) || 
                         categoryIdLower.includes(productCatLower) ||
                         // Handle common mismatches
                         (productCatLower.includes('ethnic') && categoryIdLower.includes('ethnic')) ||
                         (productCatLower.includes('blazer') && categoryIdLower.includes('blazer')) ||
                         (productCatLower.includes('shirt') && categoryIdLower.includes('shirt'));
                });
              }
              
              // Special handling for ethnic-sets: fallback to ethnic-wear size chart if available
              if (productCategory && productCategory.id === 'ethnic-sets' && !productCategory.sizeChartURLs) {
                const ethnicWearCategory = sortedCategories.find(cat => cat.id === 'ethnic-wear');
                if (ethnicWearCategory && ethnicWearCategory.sizeChartURLs) {
                  productCategory = {
                    ...productCategory,
                    sizeChartURLs: ethnicWearCategory.sizeChartURLs
                  };
                }
              }
              
              // Debug logging to see what's happening
              
              // Special debugging for ethnic products
              if (product.categoryId && product.categoryId.toLowerCase().includes('ethnic')) {
                // Ethnic product debugging logic
              }
              
              const isVisible = visibleProducts.has(product.id);
              const isAboveFold = filteredProducts.indexOf(product) < 6; // First 6 products are above the fold
              
              return (
                <Link 
                  key={`${product.id}-${windowWidth}`} 
                  to={`/product/${product.id}`}
                  state={{ 
                    productData: product,
                    categoryData: productCategory, // Pass category data including sizeChartURLs
                    referrer: window.location.href // Pass current URL with filters
                  }}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                <div 
                  className={`product-card ${product.status === 'Out of Stock' ? 'out-of-stock' : ''}`} 
                  data-product-id={product.id}
                >
                  <div className="product-image-container">
                    {/* Loading skeleton */}
                    <div 
                      className="image-skeleton" 
                      style={{ 
                        display: (isVisible || isAboveFold) ? 'block' : 'none' 
                      }}
                    >
                      <div className="skeleton-shimmer"></div>
                    </div>
                    
                    {(() => {
                      const imageUrl = getFirstImageUrl(product);
                      const shouldLoadImage = isVisible || isAboveFold;
                      
                      if (imageUrl && shouldLoadImage) {
                        return (
                          <img
                            src={imageUrl}
                            alt={product.name}
                            className="product-image"
                            loading={isAboveFold ? "eager" : "lazy"}
                            decoding="async"
                            onLoad={(e) => {
                              // Hide skeleton when image loads
                              const skeleton = e.target.parentElement.querySelector('.image-skeleton');
                              if (skeleton) skeleton.style.display = 'none';
                              e.target.style.opacity = '1';
                            }}
                            onError={(e) => {
                              // Hide skeleton on error
                              const skeleton = e.target.parentElement.querySelector('.image-skeleton');
                              if (skeleton) skeleton.style.display = 'none';
                              
                              // Create a simple fallback SVG instead of external service
                              const svgData = `
                                <svg width="400" height="500" xmlns="http://www.w3.org/2000/svg">
                                  <rect width="400" height="500" fill="#2c1810"/>
                                  <text x="200" y="250" font-family="Arial, sans-serif" font-size="16" fill="#ffffff" text-anchor="middle">
                                    No Image Available
                                  </text>
                                </svg>
                              `;
                              const blob = new Blob([svgData], { type: 'image/svg+xml' });
                              e.target.src = URL.createObjectURL(blob);
                              e.target.style.opacity = '1';
                            }}
                            style={{ 
                              opacity: '0', 
                              transition: 'opacity 0.3s ease' 
                            }}
                          />
                        );
                      } else if (!imageUrl) {
                        return (
                          <div className="no-image-placeholder">
                            <div className="no-image-icon">📷</div>
                            <div className="no-image-text">No Image</div>
                          </div>
                        );
                      } else {
                        // Don't render image if not visible and not above fold
                        return null;
                      }
                    })()}
                    
                    {/* Sold Out Badge Overlay */}
                    {product.status === 'Out of Stock' && (
                      <div className="sold-out-badge-overlay">
                        <div className="reflective-shine"></div>
                        <div className="sold-out-badge-content">
                          <span className="sold-out-text">SOLD OUT</span>
                        </div>
                      </div>
                    )}

                    {/* Discount Badge Overlay - Only show if not sold out */}
                    {product.status !== 'Out of Stock' && (() => {
                      const discountInfo = calculateProductDiscount(
                        product.id, 
                        product.categoryId, 
                        product.basePrice || 0,
                        product.basePrice || 0
                      );
                      
                      if (discountInfo.discount) {
                        const discountText = discountInfo.discount.type === 'percentage' 
                          ? `${discountInfo.discount.value}% OFF`
                          : `₹${discountInfo.discount.value} OFF`;
                        
                        return (
                          <div className="discount-badge-overlay">
                            <div className="reflective-shine"></div>
                            <div className="discount-badge-content">
                              <span className="discount-amount">₹{discountInfo.discountAmount?.toLocaleString() || '0'} OFF</span>
                              <span className="discount-separator">/</span>
                              <span className="discount-percentage">{discountInfo.discount.type === 'percentage' ? `${discountInfo.discount.value}% OFF` : ''}</span>
                            </div>
                            <span className="sparkle-1">✨</span>
                            <span className="sparkle-2">✨</span>
                            <span className="sparkle-3">✨</span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    
                    {/* View Details Button - Appears on hover */}
                    <button 
                      className="product-explore-btn"
                      onClick={(e) => {
                        // Let the parent Link handle navigation
                        e.stopPropagation();
                      }}
                      aria-label="View Product Details"
                    >
                      <span className="product-explore-btn-text">View Details</span>
                      <span className="product-explore-btn-arrow">→</span>
                    </button>
                  </div>
                  <div className="product-info">
                    <h3 className="product-name" title={product.name}>{product.name}</h3>
                    
                    {/* Product Pricing */}
                    {(() => {
                      const discountInfo = calculateProductDiscount(
                        product.id, 
                        product.categoryId, 
                        product.basePrice || 0,
                        product.basePrice || 0
                      );
                      
                      if (discountInfo.discount) {
                        const savingsAmount = product.basePrice - discountInfo.finalPrice;
                        const savingsPercentage = Math.round((savingsAmount / product.basePrice) * 100);
                        
                        return (
                          <div className="product-pricing-enhanced">
                            <div className="original-price-strikethrough">₹{product.basePrice?.toLocaleString() || '0'}</div>
                            <div className="discounted-price-main">₹{discountInfo.finalPrice?.toLocaleString() || '0'}</div>
                          </div>
                        );
                      } else {
                        return (
                          <div className="product-price">₹{product.basePrice?.toLocaleString() || '0'}</div>
                        );
                      }
                    })()}
                  </div>
                </div>
              </Link>
            );
          })}
          
          {/* Loading indicator for infinite scroll */}
          {loadingMore && (
            <div className="infinite-loading-wrapper">
              <Loader isLoading={true} />
            </div>
          )}
          
          {/* Load More Button - Fallback for users who prefer clicking */}
          {hasMore && !loadingMore && (
            <div className="load-more-container">
              <button 
                className="load-more-btn"
                onClick={loadMoreProducts}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : 'Load More Products'}
              </button>
            </div>
          )}
          
        </div>
      ) : (
        // Compact Premium "Coming Soon" section
        <div className="compact-coming-soon">
          {/* Premium Background */}
          <div className="compact-bg"></div>
          
          {/* Main Content */}
          <div className="compact-content">
            {/* Compact Header */}
            <div className="compact-header">
              <h1 className="compact-title">
                <span className="compact-main">Coming Soon</span>
                <span className="compact-collection">
                  {selectedCategory === 'all' ? 'New Collection' : selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)}
                </span>
              </h1>
            </div>
            
            {/* Brief Description */}
            <p className="compact-description">
              Our artisans are crafting pieces that embody timeless elegance and unmatched quality.
            </p>
            
            {/* Compact Feature Row */}
            <div className="compact-features">
              <div className="compact-feature">
                <div className="feature-dot">✦</div>
                <span>Premium Quality</span>
              </div>
              <div className="compact-feature">
                <div className="feature-dot">◈</div>
                <span>Handcrafted</span>
              </div>
              <div className="compact-feature">
                <div className="feature-dot">◆</div>
                <span>Exclusive Design</span>
              </div>
            </div>
            
            {/* Compact Actions */}
            <div className="compact-actions">
              <button 
                className="compact-notify-btn"
                onClick={() => {
                  alert('Thank you! We\'ll notify you when this collection is ready.');
                }}
              >
                Notify Me
              </button>
              
              <button 
                className="compact-browse-btn"
                onClick={() => {
                  setSelectedCategory('all');
                  setSelectedProductType(null);
                  setSearchParams({}); // Clear all URL parameters
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                View Collections
              </button>
            </div>
          </div>
        </div>
          )}
        </main>

    {/* Luxury Cashback Display */}
    <LuxuryCashbackDisplay 
      orderTotal={0} 
      position="bottom-right" 
      showFloatingIcon={true}
    />
  </div>
</div>
</>
);
};

export default Collections;